import React, { useMemo, useState } from "react";
import { Button, Form, InputGroup, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../_helper/CurrencyContext";
import { useDisableScrollOnFocus } from "../../../Hooks/useDisableScrollOnFocus";
import { toMoneyNumber } from "../../../utils/financeEngine";
import {
	buildGeneralServiceOptions,
	buildInvestigationOptions,
} from "../../PharmacySales/NewSales/utils/billingUtils";
import {
	formatAmount,
	formatDateList,
	normalizePaymentStatus,
} from "../utils/patientAccountUtils";
import {
	BillingInvestigationsAdjustments,
	BillingServicesAdjustments,
} from "../../PharmacySales/NewSales/components/BillingServicesSection";
import {
	LineAmountInput,
	PharmacyPurchasesBlock,
	PrescribedMedicinesBlock,
	SectionBlock,
	SectionDivider,
	SourceBadge,
	SuggestedInvestigationsBlock,
} from "./CollectPaymentBillingPanel";

const SessionStatusPill = ({ status }) => {
	const { t } = useTranslation();
	const normalized = String(status || "pending").toLowerCase();
	const isCompleted = normalized === "completed";
	return (
		<span
			className={`badge ${
				isCompleted ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"
			}`}
		>
			{isCompleted ? t("Completed") : t("Pending")}
		</span>
	);
};

const CollectPaymentTreatmentPanel = ({
	invoice,
	loading,
	normalizedInvoice,
	billingFlags,
	displayCurrency,
	defaultCurrency,
	posTaxType,
	pharmacyStateCode,
	patientStateCode,
	serviceItems,
	investigationItems,
	suggestedInvestigations = [],
	pharmacyItems,
	servicesBillingTotals,
	investigationsBillingTotals,
	billingSummary,
	servicesDiscountType,
	servicesDiscountValue,
	servicesTaxConfig,
	investigationsDiscountType,
	investigationsDiscountValue,
	investigationsTaxConfig,
	servicesDiscountLocked = false,
	servicesTaxLocked = false,
	investigationsDiscountLocked = false,
	investigationsTaxLocked = false,
	treatmentDetails,
	treatmentPaymentHistory,
	packagePrice,
	generalMasterList,
	labMasterList,
	sectionOpen,
	toggleSection,
	updateServiceAmount,
	updateServiceQuantity,
	updateInvestigationAmount,
	updateInvestigationQuantity,
	removeServiceItem,
	removeInvestigation,
	addServiceRow,
	addInvestigationRow,
	selectServiceForRow,
	selectInvestigationForRow,
	updateServicesDiscount,
	updateInvestigationsDiscount,
	updateServicesTaxConfig,
	updateInvestigationsTaxConfig,
}) => {
	const { t } = useTranslation();
	const { getCurrencySymbol, allowedCurrencies, convertCurrency } = useCurrency();
	const disableScrollProps = useDisableScrollOnFocus();

	const [readOnlyOpen, setReadOnlyOpen] = useState({
		sessions: true,
		prescribed: true,
		history: true,
	});
	const toggleReadOnly = (key) =>
		setReadOnlyOpen((prev) => ({ ...prev, [key]: !prev[key] }));

	const prescriptions = invoice?.prescriptions || [];
	const hasPharmacyPurchases = (pharmacyItems || []).length > 0;
	const isUnpaid = normalizePaymentStatus(normalizedInvoice?.status) === "unpaid";
	// Show doctor prescription when medicines are prescribed but not yet billed/dispensed
	// (do not hide just because consultation/services billing already exists).
	const showPrescribed = prescriptions.length > 0 && !hasPharmacyPurchases;
	const showPharmacySection = hasPharmacyPurchases || showPrescribed;

	const sessions = treatmentDetails?.sessions || [];

	const generalServiceOptions = useMemo(
		() =>
			buildGeneralServiceOptions(
				generalMasterList,
				getCurrencySymbol,
				defaultCurrency,
				allowedCurrencies
			),
		[allowedCurrencies, defaultCurrency, generalMasterList, getCurrencySymbol]
	);

	const labInvestigationOptions = useMemo(
		() =>
			buildInvestigationOptions(
				labMasterList,
				getCurrencySymbol,
				defaultCurrency,
				allowedCurrencies
			),
		[allowedCurrencies, defaultCurrency, getCurrencySymbol, labMasterList]
	);

	const usedServiceIds = new Set(
		serviceItems.filter((item) => item.service_id).map((item) => String(item.service_id))
	);

	const isReadOnlyService = (item) => !item.isNewRow || item.isConsultation;
	const isSelectableService = (item) => !item.isConsultation && Boolean(item.isNewRow);
	const isReadOnlyInvestigation = (item) => !item.isNewRow;
	const isSelectableInvestigation = (item) => Boolean(item.isNewRow);

	const showServicesTax = billingFlags?.showServicesTax === true;
	const showLabTax = billingFlags?.showLabTax === true;

	const amountInputProps = {
		displayCurrency,
		convertCurrency,
		pharmacyCurrency: defaultCurrency,
		allowedCurrencies,
		disableScrollProps,
	};

	if (loading) {
		return <div className="cp-loading">{t("Loading treatment details...")}</div>;
	}

	return (
		<div className="cp-billing-panel">
			<div className="cp-tp-details-card">
				<div className="cp-tp-details-head">
					<span className="badge tp-type-badge">{t("Treatment Package")}</span>
				</div>
				<div className="cp-tp-details-grid">
					<div className="cp-tp-detail">
						<span className="cp-tp-detail-label">{t("Package Name")}</span>
						<span className="cp-tp-detail-value">{treatmentDetails?.packageName || "-"}</span>
					</div>
					<div className="cp-tp-detail">
						<span className="cp-tp-detail-label">{t("Package Price")}</span>
						<span className="cp-tp-detail-value">
							{formatAmount(packagePrice, displayCurrency, getCurrencySymbol)}
						</span>
					</div>
					<div className="cp-tp-detail">
						<span className="cp-tp-detail-label">{t("Session")}</span>
						<span className="cp-tp-detail-value">
							{treatmentDetails?.currentSession || "-"}
							{treatmentDetails?.noOfSessions
								? ` (${treatmentDetails.noOfSessions} ${t("sessions")})`
								: ""}
						</span>
					</div>
					<div className="cp-tp-detail">
						<span className="cp-tp-detail-label">{t("Collected Amount")}</span>
						<span className="cp-tp-detail-value">
							{formatAmount(
								treatmentDetails?.totalCollected,
								displayCurrency,
								getCurrencySymbol
							)}
						</span>
					</div>
				</div>

				<div className="cp-tp-sessions">
					<button
						type="button"
						className="cp-tp-sessions-toggle"
						onClick={() => toggleReadOnly("sessions")}
						aria-expanded={readOnlyOpen.sessions}
					>
						<span>
							<i className="fa fa-list-ul me-1" aria-hidden="true" />
							{t("Sessions")}
							{sessions.length > 0 && (
								<span className="cp-tp-sessions-count">({sessions.length})</span>
							)}
						</span>
						<i
							className={`fa fa-chevron-${readOnlyOpen.sessions ? "up" : "down"}`}
							aria-hidden="true"
						/>
					</button>
					{readOnlyOpen.sessions && (
						<div className="cp-tp-sessions-body">
							<div className="table-responsive cp-table-wrap">
								<Table size="sm" className="cp-table mb-0">
									<thead>
										<tr>
											<th>#</th>
											<th>{t("Session")}</th>
											<th>{t("Duration")}</th>
											<th>{t("Schedule")}</th>
											<th className="text-center">{t("Status")}</th>
										</tr>
									</thead>
									<tbody>
										{sessions.length > 0 ? (
											sessions.map((session) => (
												<tr key={session.id}>
													<td>{session.index}</td>
													<td className="fw-semibold">{session.sessionName}</td>
													<td>{session.duration}</td>
													<td>{session.schedule}</td>
													<td className="text-center">
														<SessionStatusPill status={session.status} />
													</td>
												</tr>
											))
										) : (
											<tr>
												<td colSpan={5} className="text-center text-muted py-3">
													{t("No sessions found.")}
												</td>
											</tr>
										)}
									</tbody>
								</Table>
							</div>
						</div>
					)}
				</div>
			</div>

			<SectionBlock
				accent="services"
				icon="fa-plus-square-o"
				title={t("Treatment & Other Services")}
				total={toMoneyNumber(
					billingSummary.services_total ??
						(billingSummary.consultation_total ?? 0) +
							(billingSummary.additional_services_total ?? 0)
				)}
				currency={displayCurrency}
				getCurrencySymbol={getCurrencySymbol}
				isOpen={sectionOpen.services}
				onToggle={() => toggleSection("services")}
				itemCount={serviceItems.length}
				footer={
					<>
						<SectionDivider title={t("Discount & Tax")} />
						<div className="cp-adjustments-body">
							<BillingServicesAdjustments
								t={t}
								servicesBilling={servicesBillingTotals}
								servicesTaxConfig={servicesTaxConfig}
								servicesDiscountType={servicesDiscountType}
								servicesDiscountValue={servicesDiscountValue}
								onDiscountTypeChange={(type) =>
									updateServicesDiscount({ discountType: type })
								}
								onDiscountValueChange={(value) =>
									updateServicesDiscount({ discountValue: value })
								}
								onTaxConfigChange={updateServicesTaxConfig}
								displayCurrency={displayCurrency}
								getCurrencySymbol={getCurrencySymbol}
								posTaxType={posTaxType}
								pharmacyStateCode={pharmacyStateCode}
								patientStateCode={patientStateCode}
								disableScrollProps={disableScrollProps}
								showTax={showServicesTax}
								disabledDiscount={servicesDiscountLocked}
								disabledTax={servicesTaxLocked}
							/>
						</div>
					</>
				}
			>
				<div className="table-responsive cp-table-wrap">
					<Table size="sm" className="cp-table mb-0">
						<thead>
							<tr>
								<th>{t("Service")}</th>
								<th className="cp-col-qty">{t("Qty")}</th>
								<th className="cp-col-amount">{t("Amount")}</th>
								<th className="cp-col-action" />
							</tr>
						</thead>
						<tbody>
							{serviceItems.length > 0 ? (
								serviceItems.map((item) => (
									<tr key={item.id}>
										<td>
											{isReadOnlyService(item) ? (
												<div className="cp-line-name">
													<span>{item.service_name}</span>
													{item.isConsultation && (
														<SourceBadge variant="consultation">{t("Consultation")}</SourceBadge>
													)}
													{item.isPrescribed && !item.isConsultation && (
														<SourceBadge variant="prescribed">{t("Prescribed")}</SourceBadge>
													)}
													{item.status && (
														<SourceBadge
															variant={
																String(item.status).toLowerCase() === "completed"
																	? "completed"
																	: "pending"
															}
														>
															{String(item.status).toLowerCase() === "completed"
																? t("Completed")
																: t("Pending")}
														</SourceBadge>
													)}
												</div>
											) : (
												<Form.Select
													size="sm"
													value={item.service_id || ""}
													onChange={(e) => selectServiceForRow(item.id, e.target.value)}
													{...disableScrollProps}
												>
													<option value="">{t("Select service")}</option>
													{generalServiceOptions.map((opt) => (
														<option
															key={opt.value}
															value={opt.value}
															disabled={
																usedServiceIds.has(String(opt.value)) &&
																String(item.service_id) !== String(opt.value)
															}
														>
															{opt.label}
														</option>
													))}
												</Form.Select>
											)}
										</td>
										<td>
											<Form.Control
												type="number"
												size="sm"
												min={1}
												className="cp-qty-input"
												value={item.quantity ?? 1}
												disabled={isReadOnlyService(item) || !item.service_id}
												onChange={(e) =>
													updateServiceQuantity(
														item.id,
														Math.max(1, parseInt(e.target.value, 10) || 1)
													)
												}
												{...disableScrollProps}
											/>
										</td>
										<td className="text-end">
											<InputGroup size="sm" className="cp-amount-group">
												<InputGroup.Text>{getCurrencySymbol(displayCurrency)}</InputGroup.Text>
												<LineAmountInput
													item={item}
													updateFn={updateServiceAmount}
													disabled={isReadOnlyService(item) || !item.service_id}
													{...amountInputProps}
												/>
											</InputGroup>
										</td>
										<td className="cp-col-action">
											{isSelectableService(item) && (
												<button
													type="button"
													className="cp-remove-btn"
													onClick={() => removeServiceItem(item.id)}
													aria-label={t("Remove service")}
												>
													<i className="fa fa-trash" />
												</button>
											)}
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={4} className="text-center text-muted py-3">
										{t("No additional services added")}
									</td>
								</tr>
							)}
						</tbody>
					</Table>
				</div>
				<Button variant="outline-primary" size="sm" className="cp-add-btn" onClick={addServiceRow}>
					<i className="fa fa-plus me-1" />
					{t("Add Service")}
				</Button>
			</SectionBlock>

			<SectionBlock
				accent="investigations"
				icon="fa-flask"
				title={t("Lab Investigations")}
				total={billingSummary.investigation_total ?? 0}
				currency={displayCurrency}
				getCurrencySymbol={getCurrencySymbol}
				isOpen={sectionOpen.investigations}
				onToggle={() => toggleSection("investigations")}
				itemCount={investigationItems.length}
				footer={
					<>
						<SectionDivider title={t("Discount & Tax")} />
						<div className="cp-adjustments-body">
							<BillingInvestigationsAdjustments
								t={t}
								investigationsBilling={investigationsBillingTotals}
								investigationsTaxConfig={investigationsTaxConfig}
								investigationsDiscountType={investigationsDiscountType}
								investigationsDiscountValue={investigationsDiscountValue}
								onDiscountTypeChange={(type) =>
									updateInvestigationsDiscount({ discountType: type })
								}
								onDiscountValueChange={(value) =>
									updateInvestigationsDiscount({ discountValue: value })
								}
								onTaxConfigChange={updateInvestigationsTaxConfig}
								displayCurrency={displayCurrency}
								getCurrencySymbol={getCurrencySymbol}
								posTaxType={posTaxType}
								pharmacyStateCode={pharmacyStateCode}
								patientStateCode={patientStateCode}
								disableScrollProps={disableScrollProps}
								showTax={showLabTax}
								disabledDiscount={investigationsDiscountLocked}
								disabledTax={investigationsTaxLocked}
							/>
						</div>
					</>
				}
			>
				<SuggestedInvestigationsBlock suggestions={suggestedInvestigations} t={t} />
				{suggestedInvestigations.length > 0 && investigationItems.length > 0 && (
					<div className="cp-section-divider" />
				)}
				<div className="table-responsive cp-table-wrap">
					<Table size="sm" className="cp-table mb-0">
						<thead>
							<tr>
								<th>{t("Test")}</th>
								<th className="cp-col-qty">{t("Qty")}</th>
								<th className="cp-col-amount">{t("Amount")}</th>
								<th className="cp-col-action" />
							</tr>
						</thead>
						<tbody>
							{investigationItems.length > 0 ? (
								investigationItems.map((item) => (
									<tr key={item.id}>
										<td>
											{isReadOnlyInvestigation(item) ? (
												<div className="cp-line-name">
													<span>{item.test_name}</span>
													<SourceBadge variant="prescribed">{t("Prescribed")}</SourceBadge>
													{item.status && (
														<SourceBadge
															variant={
																String(item.status).toLowerCase() === "completed"
																	? "completed"
																	: "pending"
															}
														>
															{String(item.status).toLowerCase() === "completed"
																? t("Completed")
																: t("Pending")}
														</SourceBadge>
													)}
												</div>
											) : (
												<Form.Select
													size="sm"
													value={item.test_id || ""}
													onChange={(e) => selectInvestigationForRow(item.id, e.target.value)}
													{...disableScrollProps}
												>
													<option value="">{t("Select test")}</option>
													{labInvestigationOptions.map((opt) => (
														<option
															key={opt.value}
															value={opt.value}
														>
															{opt.label}
														</option>
													))}
												</Form.Select>
											)}
										</td>
										<td>
											<Form.Control
												type="number"
												size="sm"
												min={1}
												className="cp-qty-input"
												value={item.quantity ?? 1}
												disabled={isReadOnlyInvestigation(item) || !item.test_id}
												onChange={(e) =>
													updateInvestigationQuantity(
														item.id,
														Math.max(1, parseInt(e.target.value, 10) || 1)
													)
												}
												{...disableScrollProps}
											/>
										</td>
										<td className="text-end">
											<InputGroup size="sm" className="cp-amount-group">
												<InputGroup.Text>{getCurrencySymbol(displayCurrency)}</InputGroup.Text>
												<LineAmountInput
													item={item}
													updateFn={updateInvestigationAmount}
													disabled={isReadOnlyInvestigation(item) || !item.test_id}
													{...amountInputProps}
												/>
											</InputGroup>
										</td>
										<td className="cp-col-action">
											{isSelectableInvestigation(item) && (
												<button
													type="button"
													className="cp-remove-btn"
													onClick={() => removeInvestigation(item.id)}
													aria-label={t("Remove investigation")}
												>
													<i className="fa fa-trash" />
												</button>
											)}
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={4} className="text-center text-muted py-3">
										{t("No lab tests added")}
									</td>
								</tr>
							)}
						</tbody>
					</Table>
				</div>
				<Button
					variant="outline-primary"
					size="sm"
					className="cp-add-btn"
					onClick={addInvestigationRow}
				>
					<i className="fa fa-plus me-1" />
					{t("Add Investigation")}
				</Button>
			</SectionBlock>

			{showPharmacySection && (
				<SectionBlock
					accent="medicines"
					icon="fa-medkit"
					title={t("Pharmacy")}
					total={hasPharmacyPurchases ? billingSummary.pharmacy_total ?? 0 : null}
					showTotal={hasPharmacyPurchases}
					currency={displayCurrency}
					getCurrencySymbol={getCurrencySymbol}
					isOpen={sectionOpen.medicines}
					onToggle={() => toggleSection("medicines")}
					itemCount={hasPharmacyPurchases ? pharmacyItems.length : prescriptions.length}
				>
					{showPrescribed && (
						<PrescribedMedicinesBlock prescriptions={prescriptions} t={t} />
					)}
					{hasPharmacyPurchases && showPrescribed && (
						<div className="cp-section-divider" />
					)}
					<PharmacyPurchasesBlock
						pharmacyItems={pharmacyItems}
						displayCurrency={displayCurrency}
						getCurrencySymbol={getCurrencySymbol}
						t={t}
					/>
				</SectionBlock>
			)}

			<SectionBlock
				accent="services"
				icon="fa-history"
				title={t("Payment History")}
				showTotal={false}
				isOpen={readOnlyOpen.history}
				onToggle={() => toggleReadOnly("history")}
				itemCount={treatmentPaymentHistory.length}
			>
				<div className="table-responsive cp-table-wrap">
					<Table size="sm" className="cp-table mb-0">
						<thead>
							<tr>
								<th>{t("Date")}</th>
								<th>{t("Type")}</th>
								<th>{t("Description")}</th>
								<th className="text-end">{t("Paid Amount")}</th>
								<th>{t("Payment Method")}</th>
							</tr>
						</thead>
						<tbody>
							{treatmentPaymentHistory.length > 0 ? (
								treatmentPaymentHistory.map((payment, index) => (
									<tr key={index}>
										<td>{formatDateList(payment.date)}</td>
										<td>{payment.type}</td>
										<td className="text-muted" style={{ whiteSpace: "pre-line" }}>
											{payment.description}
										</td>
										<td className="text-end fw-semibold">
											{payment.is_payment
												? formatAmount(
														payment.paid_amount,
														payment.currency,
														getCurrencySymbol
												  )
												: ""}
										</td>
										<td style={{ textTransform: "capitalize" }}>
											{payment.is_payment ? payment.payment_mode || "-" : ""}
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={5} className="text-center text-muted py-3">
										{t("No previous payments recorded.")}
									</td>
								</tr>
							)}
						</tbody>
					</Table>
				</div>
			</SectionBlock>
		</div>
	);
};

export default CollectPaymentTreatmentPanel;
